import { useIntl } from 'react-intl';

type FormattedNumberWithImpactedDigitsProps = {
  value: number;
  precision: number;
  impactedDigits: number;
};

/**
 * Renders a formatted number with the last `impactedDigits` digits wrapped in a <span> for highlighting.
 *
 * @param props - The component props.
 * @param props.value - The number to format and display.
 * @param props.precision - The number of decimal digits to display.
 * @param props.impactedDigits - The count of trailing digits to highlight.
 *
 * @example
 * // Displays "1,234.5678" with the last 2 digits ("78") wrapped in a <span>
 * <FormattedNumberWithImpactedDigits value={1234.5678} precision={4} impactedDigits={2} />
 */
export const FormattedNumberWithImpactedDigits = (props: FormattedNumberWithImpactedDigitsProps) => {
  const { value, precision, impactedDigits } = props;

  const { formatNumber } = useIntl();

  const formattedValue = formatNumber(value, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });

  const { normalPart, highlightedPart } = splitFormattedNumber(formattedValue, impactedDigits);

  return (
    <>
      {normalPart}
      <span className="text-warning">{highlightedPart}</span>
    </>
  );
};

/**
 * Splits a formatted number string into a normal part and a highlighted trailing part.
 * Only numeric digits are counted — separators (`.`, `,`, etc.) are ignored in the count.
 *
 * @param formattedValue - The formatted number string (e.g. "1,234.56").
 * @param impactedDigits - The number of trailing digits to include in the highlighted part.
 * @returns An object with `normalPart` and `highlightedPart` strings.
 */
function splitFormattedNumber(
  formattedValue: string,
  impactedDigits: number,
): { normalPart: string; highlightedPart: string } {
  if (impactedDigits <= 0) {
    return { normalPart: formattedValue, highlightedPart: '' };
  }

  const splitIndex = Array.from(formattedValue).reduceRight(
    (accumulator, character, index) => {
      const digitCount = accumulator.digitCount + (/\d/.test(character) ? 1 : 0);
      const splitIndex = digitCount === impactedDigits ? index : accumulator.splitIndex;

      return { digitCount, splitIndex };
    },
    { digitCount: 0, splitIndex: formattedValue.length },
  ).splitIndex;

  return {
    normalPart: formattedValue.slice(0, splitIndex),
    highlightedPart: formattedValue.slice(splitIndex),
  };
}
